// Shared PDF text extraction + POS line parser for product imports.
// SingleDayUpload.jsx and PdfUpload.jsx have their own (slightly older,
// debug-laden) copies of these helpers — we deliberately do NOT touch
// those to keep the production data-entry paths unchanged. New callers
// should use this module.

// Shared worker setup — matches the pdfjs version installed in node_modules.
// Replaces the previous fetch-with-fallbacks dance that broke whenever the
// public/pdf.worker.min.mjs copy drifted from the npm package version.
import { configurePdfWorker } from './pdfWorker'
async function setupPdfWorker() {
  configurePdfWorker()
}

/** Extract text from a PDF file, reconstructing tabular layout from
 *  position data. Returns the full document text with newline-separated
 *  rows and multi-space column separators. */
export async function extractPdfText(file, onProgress) {
  onProgress?.('Loading PDF library...')
  const pdfjsLib = await import('pdfjs-dist')
  await setupPdfWorker()

  onProgress?.('Reading file...')
  const arrayBuffer = await file.arrayBuffer()

  onProgress?.('Parsing PDF structure...')
  const loadPromise = pdfjsLib.getDocument(arrayBuffer).promise
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('PDF loading timed out after 60 seconds. Try a smaller file.')), 60000)
  )
  const pdf = await Promise.race([loadPromise, timeoutPromise])

  let fullText = ''
  for (let i = 1; i <= pdf.numPages; i++) {
    onProgress?.(`Extracting text from page ${i} of ${pdf.numPages}...`)
    try {
      const page = await pdf.getPage(i)
      const content = await page.getTextContent()
      const items = content.items.filter(item => item.str.trim())
      if (items.length === 0) continue
      const sorted = [...items].sort((a, b) => {
        const yDiff = b.transform[5] - a.transform[5]
        if (Math.abs(yDiff) > 3) return yDiff
        return a.transform[4] - b.transform[4]
      })
      const rows = []
      let currentRow = []
      let lastY = null
      for (const item of sorted) {
        const y = Math.round(item.transform[5])
        if (lastY !== null && Math.abs(y - lastY) > 3) {
          if (currentRow.length > 0) rows.push(currentRow)
          currentRow = []
        }
        currentRow.push(item)
        lastY = y
      }
      if (currentRow.length > 0) rows.push(currentRow)
      for (const row of rows) {
        row.sort((a, b) => a.transform[4] - b.transform[4])
        let lineText = ''
        let lastX = 0
        for (const item of row) {
          const x = item.transform[4]
          const gap = x - lastX
          if (lineText && gap > 20) lineText += '   '
          else if (lineText && gap > 5) lineText += ' '
          lineText += item.str
          lastX = x + (item.width || item.str.length * 5)
        }
        fullText += lineText + '\n'
      }
    } catch (err) {
      console.warn(`Failed to parse page ${i}:`, err)
      fullText += `\n[Page ${i} failed to parse]\n`
    }
  }
  return fullText
}

/** Section headers found in real POS reports. A line whose lowercase
 *  text exactly matches one of these is treated as a section marker,
 *  not a product row. We deliberately keep this list short and exact
 *  to avoid false positives on product names like "Bakery Slice". */
export const POS_SECTION_HEADERS = new Set([
  'kitchen', 'bakery', 'sandwich', 'sandwiches',
  'soft drink', 'soft drinks', 'drinks',
  'tea', 'sweets', 'sweets & chips', 'sweets and chips', 'chips',
  'not grouped', 'miscellaneous', 'misc',
  'pin', 'cff', 'other',
  'tuckshop', 'tuck shop', 'yawehs', "yaweh's delight",
])

/** Parse a single POS product line.
 *
 *  Returns { name, qty, sales, salesRands, productCode } or null if
 *  the line is a header, section marker, total, or non-product line.
 *
 *  POS line format (% column is the anchor, always present):
 *    {CODE} {NAME...} {QTY} {SALES_R} {%SALES}% {COST_R} {PROFIT_R}
 *
 *  CODE can be alphanumeric (MAM, mts, f2, SB30) or a 13-digit barcode.
 *  Subtotal lines have no leading code and start with a number — they
 *  are filtered by the pure-digit guard on the captured name slot.
 *
 *  `name` is the FULL captured text including the leading code (kept
 *  for backward compatibility with BulkProductUpload's name→code
 *  generator and the existing fuzzyMatch pipeline). `productCode` is
 *  the first whitespace-delimited token, which lets new callers do
 *  exact code matching.
 */
export function parsePosLine(line) {
  if (line == null) return null
  const raw = String(line)
  const trimmed = raw.trim()
  if (!trimmed) return null

  // Skip page-header / report-header noise
  if (/^(page\s+\d|group\s+qty|makhado\s+park|for\s+the\s+period|staff\s+pos|sales\s+r\s+cost)/i.test(trimmed)) return null
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) return null

  // Skip section headers (Bakery, Kitchen, Soft Drink, etc.)
  if (POS_SECTION_HEADERS.has(trimmed.toLowerCase())) return null

  // Skip subtotal rows: line where the leading character (before trim)
  // is whitespace and the first non-whitespace token is a number. The
  // strategies below also catch this via the pure-digit name guard,
  // but doing it up-front is faster and more explicit.
  if (/^\s+\d/.test(raw) && !/[A-Za-z]/.test(raw.split(/\s+/).slice(0, 2).join(''))) {
    return null
  }

  // Strategy 1: % column as anchor — NAME  QTY  SALES  PERCENT%  ...
  const pctMatch = trimmed.match(/^(.+?)\s+(\d+)\s+(-?\d+(?:\.\d+)?)\s+\d+\.\d+%/)
  if (pctMatch) {
    const name = pctMatch[1].trim()
    const qty = parseInt(pctMatch[2], 10)
    const sales = parseFloat(pctMatch[3])
    if (name && !isNaN(qty) && !isNaN(sales) && !/^\d+(\.\d+)?$/.test(name)) {
      const productCode = name.split(/\s+/)[0] || ''
      return { name, productCode, qty, sales, salesRands: sales }
    }
  }

  // Strategy 2: column split by 2+ spaces — first numeric pair after name
  const parts = trimmed.split(/\s{2,}|\t/).filter(p => p.trim())
  if (parts.length >= 3) {
    const name = parts[0].trim()
    if (name && !/^\d+(\.\d+)?$/.test(name)) {
      const qty = parseInt(parts[1], 10)
      const sales = parseFloat((parts[2] || '').replace(/[R,]/g, ''))
      if (!isNaN(qty) && !isNaN(sales)) {
        const productCode = name.split(/\s+/)[0] || ''
        return { name, productCode, qty, sales, salesRands: sales }
      }
    }
  }

  // Strategy 3: greedy name match
  const greedyMatch = trimmed.match(/^(.+?)\s+(\d+)\s+(\d+\.\d{2})\s/)
  if (greedyMatch) {
    const name = greedyMatch[1].trim()
    const qty = parseInt(greedyMatch[2], 10)
    const sales = parseFloat(greedyMatch[3])
    if (name && !isNaN(qty) && !isNaN(sales) && !/^\d+(\.\d+)?$/.test(name)) {
      const productCode = name.split(/\s+/)[0] || ''
      return { name, productCode, qty, sales, salesRands: sales }
    }
  }

  return null
}
